import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { Icon, Dropdown, Menu, Modal, Button, Radio, message, Tag, Popover, Badge, Checkbox, Tooltip } from 'antd';
import FileDownload from 'js-file-download';
import Tour from 'reactour';

import * as customersActions from '~/_domains/customers/drawer/actions';
import CustomerTasks from '../components/CustomerTasks';

import moment from 'moment';
import { request } from '~/_shared/instance';
import { PERMISSIONS } from '~/constants/permissions.constants';
import { ModuleHeader } from '~/components/ModuleHeader';
import Tip from '~/components/Tip';
import { RadioButtonWrapper } from '~/components/UI/RadioButtonWrapper';
import ImportModal from './Import.jsx';
import CustomersVirtualTable from './VirtualTable';
import { useAppContext } from '~/AppContext';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import { CUSTOMERS_FILTERS_KEY, getDefaultCustomersFilters, countActiveCustomersFilters } from './filters';
import SelectionToolbar from '~/_shared/components/SelectionToolbar';
import { customerNeedsRule, CUSTOMER_RULE_HELP } from '~/_domains/tasks/enrollment';
import { openCustomerOnTab } from '~/_domains/customers/drawer/openTab';

// Chaves da gaveta do cliente que dá para mudar em massa (o backend aceita só estas).
const BULK_FLAGS = [
  { field: 'show_in_federal', label: 'Malha Fiscal: exibir no federal', help: 'Mostra ou tira esses clientes da lista federal da Malha Fiscal.' },
  { field: 'show_in_state', label: 'Malha Fiscal: exibir no estadual', help: 'Mostra ou tira esses clientes da lista estadual da Malha Fiscal.' },
  { field: 'show_in_municipal', label: 'Malha Fiscal: exibir no municipal', help: 'Mostra ou tira esses clientes da lista municipal da Malha Fiscal.' },
  { field: 'show_in_fgts', label: 'Malha Fiscal: exibir no FGTS', help: 'Mostra ou tira esses clientes da lista de FGTS da Malha Fiscal.' },
  { field: 'conciliation', label: 'Conciliação', help: 'Marca se o escritório faz a conciliação bancária desses clientes.' },
  { field: 'allow_nf', label: 'Emissão de NF-e', help: 'Permite ou não que esses clientes peçam a emissão de NF-e pelo portal (as solicitações aparecem em Solicitações).' },
];

const CustomersList = (props) => {
  // Filtros mantidos por alguns minutos após o refresh (por escritório/usuário/página).
  // A lista vem do AppContext e é filtrada aqui, então nada precisa recarregar.
  const persisted = usePersistedFilters(CUSTOMERS_FILTERS_KEY, getDefaultCustomersFilters);
  const setFilters = persisted.setFilters;
  // Busca aplicada (salva); o campo digita em `searchInput` com debounce de 300ms.
  // Uma busca restaurada já começa aplicada.
  const search = persisted.filters.search;
  const actived = persisted.filters.actived;
  // Só os clientes ativos sem regra: a pendência que a faixa acima da lista conta.
  const onlyNoRule = persisted.filters.onlyNoRule;
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    if (searchInput === search) return undefined;
    const timer = setTimeout(() => { setFilters({ search: searchInput }); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const setActived = (value) => setFilters({ actived: value });
  const setOnlyNoRule = (value) => setFilters({ onlyNoRule: value });
  const clearSearch = () => { setSearchInput(''); setFilters({ search: '' }); };
  const handleClearAll = () => { setSearchInput(''); persisted.resetFilters(); };
  const activeFilterCount = countActiveCustomersFilters(persisted.filters, persisted.defaults);
  const restoredSummary = [
    actived !== persisted.defaults.actived ? ({ 1: 'Ativos', 2: 'Pendentes', 0: 'Inativos' }[actived] || '') : '',
    onlyNoRule ? 'Sem regra' : '',
    search ? 'Busca: ' + search : '',
  ].filter(Boolean).join(' · ');
  const [importCSV, setImportCSV] = useState(false);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [tasksDrawer, setTasksDrawer] = useState({
    visible: false,
    customer: null
  });
  // Clientes marcados na tabela para as ações em massa (id → true).
  const [selected, setSelected] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  useEffect(() => { setSelected({}); }, [actived]);
  const [steps, setSteps] = useState([
    {
      selector: '[data-tour="cadastrar-cliente"]',
      content: 'Clique aqui para cadastrar clientes, você pode cadastrar manualmente ou importar via Excel.',
    },
  ]);
  const { onboardingData, setOnboardingData } = useAppContext();

  useEffect(() => {
    const importCustomersStep = onboardingData.find(step => step.id === 'm3n4o5');
    if (importCustomersStep && importCustomersStep.status === 'pending') {
      setIsTourOpen(true);
    }
  }, [onboardingData]);

  const onAction = useCallback((e, customer) => {
    switch(e.key){
      case 'details': 
        props.showDetails(customer);
        break;
      case 'inactivate': 
        props.inactivateCustomer(customer.id);
        break;
      case 'activate': 
        props.activateCustomer(customer.id);
        break;
      case 'force_activate':
        Modal.confirm({
          title: 'Forçar ativação',
          content: <div>Este cliente será <strong>automaticamente ativado após a conclusão do processo no Dep. Societário</strong>. Você tem certeza que deseja forçar a ativação? <br />*Esta ação não poderá ser desfeita. <br/>*O cliente ativo será listado em tarefas e no módulo financeiro.</div>,
          okText: 'Sim',
          okType: 'danger',
          cancelText: 'Cancelar',
          onOk: () => props.forceActivateCustomer(customer.id)
        });
        break;
      case 'edit':
        props.editCustomer(customer);
        break;
      case 'tasks':
        setTasksDrawer({
          visible: true,
          customer
        });
        break;
      case 'delete':
        Modal.confirm({
          title: 'Você realmente deseja excluir este cliente?',
          content: 'Esta ação não poderá ser desfeita.',
          okText: 'Sim',
          okType: 'danger',
          onOk: () => props.deleteCustomer(customer.id)
        });
        break;
      default:
        return;
    }
  }, [props]);

  const handleCancel = () => setImportCSV(false);

  const exportToExcel = async () => {
    try {
      const response = await request(
        'POST',
        '/customers/export',
        {
          status: actived ? 'actived' : 'inactived'
        },
        true,
        {
          responseType: 'blob'
        }
      );
  
      const now = moment().format('DD_MM_YYYY_HH-mm-ss');
      FileDownload(response.data, `Clientes_${actived ? 'Ativos' : 'Inativos'}_${now}.xlsx`);
    } catch (error) {
      message.error('Não foi possível exportar os clientes para Excel. Tente novamente mais tarde.');
    }
  };

  const hasContract = (customer) => {
    return customer.has_contract === 1;
  };

  const hasRescission = (customer) => customer.has_rescission === 1;

  const styleCompanyName = (customer) => {
    if(hasRescission(customer)){
      return {textDecoration: 'line-through', color: 'red'};
    }
    if(hasContract(customer)){
      return {fontWeight: 'bold', color: '#52c41a'};
    }
    return {};
  };

  const handleCopyToClipBoard = useCallback(() => {
    const domain = process.env.REACT_APP_CUSTOMER_DOMAIN;
    const link = `http://${domain}`;
    navigator.clipboard.writeText(link);
    message.success("Link da área do cliente copiado para a área de transferência!");
  }, []);

  const {
    customers,
    user,
    isLoading,
    companyTypes
  } = props;

  let filterCompanyType = [];
  companyTypes.map(item => filterCompanyType.push({text: item.value, value: item.id}));

  const formattedCustomers = customers
    ? customers.map((customer) => {
        const { doc_delivery_method /*, generated_app_user, app_users */ } = customer;

        if (
          doc_delivery_method !== 2 /* ||
          !generated_app_user ||
          app_users.length === 0 */
        ) {
          return {
            ...customer,
            most_recent_app_user: null,
          };
        }

        return {
          ...customer,
          // most_recent_app_user: app_users[0]
        };
      })
    : [];

  const filteredCustomers = useMemo(() => {
    return formattedCustomers.filter((str) => {

      if (onlyNoRule && !customerNeedsRule(str)) {
        return false;
      }

      if((actived === 1 || actived === 0) && !str.enabled){
        return false;
      }

      if(actived === 2 && str.enabled){
        return false;
      }

      if((str.inactived_at === null && !actived) || (str.inactived_at !== null && actived)){
        return false;
      }

      // Exclude deleted customers from Inativos list
      if(actived === 0 && str.deleted_at !== null){
        return false;
      }

      if(str.name.toLowerCase().indexOf(search.toLowerCase()) >= 0){
        return true;
      }

      if(str.email !== null && str.email.toLowerCase().indexOf(search.toLowerCase()) >= 0){
        return true;
      }

      if(str.identification_number !== null && str.identification_number.toString().toLowerCase().indexOf(search.toLowerCase()) >= 0){
        return true;
      }

      if(str.cnpj_formated !== null && str.cnpj_formated.toLowerCase().indexOf(search.toLowerCase()) >= 0){
        return true;
      }

      if(str.cod !== null && str.cod.toLowerCase().indexOf(search.toLowerCase()) >= 0){
        return true;
      }

      return false;
    });
  }, [formattedCustomers, actived, search, onlyNoRule]);

  const noRuleCount = useMemo(
    () => formattedCustomers.filter((customer) => customerNeedsRule(customer)).length,
    [formattedCustomers] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Abre o cadastro do cliente direto na aba Regra.
  const openRule = useCallback((customer) => {
    openCustomerOnTab('regra');
    props.editCustomer(customer);
  }, [props]);

  var statusCounts = useMemo(function() {
    var active = 0;
    var pending = 0;
    var inactive = 0;
    for (var i = 0; i < formattedCustomers.length; i++) {
      var c = formattedCustomers[i];
      if (!c.enabled) continue;
      if (c.inactived_at !== null && c.deleted_at === null) { inactive++; }
      else if (!c.enabled) { /* skip */ }
      else if (c.inactived_at === null && c.enabled) {
        // enabled + not inactivated: check if pending (enabled=2 means pending in the filter logic)
        // Actually the filter uses: actived===2 && str.enabled → false, actived===1 && !str.enabled → false
        // So enabled customers with inactived_at===null are "Ativos" (actived=1)
        // and !enabled customers are "Pendentes" (actived=2)
        active++;
      }
    }
    // Re-count properly: the filter logic is:
    // actived=1: enabled && inactived_at===null
    // actived=2: !enabled
    // actived=0: enabled && inactived_at!==null && deleted_at===null
    active = 0; pending = 0; inactive = 0;
    for (var j = 0; j < formattedCustomers.length; j++) {
      var cu = formattedCustomers[j];
      if (cu.enabled && cu.inactived_at === null) active++;
      else if (!cu.enabled) pending++;
      else if (cu.enabled && cu.inactived_at !== null && cu.deleted_at === null) inactive++;
    }
    return { 1: active, 2: pending, 0: inactive };
  }, [formattedCustomers]);

  const canCreateCustomers = user.permissions.includes(PERMISSIONS.customers.create);
  const canEditCustomers = user.permissions.includes(PERMISSIONS.customers.edit);
  const canDeleteCustomers = user.permissions.includes(PERMISSIONS.customers.delete);
  const canBulkExtrato = canEditCustomers && user.permissions.includes(PERMISSIONS.extratoBancario.view);

  const selectedIds = Object.keys(selected).map(Number);
  const selectedCount = selectedIds.length;
  const filteredSelectedCount = filteredCustomers.filter(c => selected[c.id]).length;
  const allSelected = filteredCustomers.length > 0 && filteredSelectedCount === filteredCustomers.length;

  const toggleRow = useCallback((id) => {
    setSelected(current => {
      const next = { ...current };
      if (next[id]) { delete next[id]; } else { next[id] = true; }
      return next;
    });
  }, []);

  // "Todos" é a lista filtrada inteira, não só as linhas na tela.
  const toggleAll = useCallback((checked) => {
    setSelected(current => {
      const next = { ...current };
      filteredCustomers.forEach(c => {
        if (checked) { next[c.id] = true; } else { delete next[c.id]; }
      });
      return next;
    });
  }, [filteredCustomers]);

  const bulkExtrato = (enabled) => {
    const chosen = formattedCustomers.filter(c => selected[c.id]);
    const already = chosen.filter(c => (Number(c.extrato_bancario) === 1) === enabled).length;
    const toChange = chosen.length - already;
    const n = chosen.length;

    Modal.confirm({
      title: (enabled ? 'Ligar' : 'Desligar') + ' a aba Extrato Bancário para ' + n + (n === 1 ? ' cliente?' : ' clientes?'),
      content: (
        <div>
          {enabled
            ? 'Esses clientes passam a ver a aba Extrato Bancário no portal e a receber o lembrete mensal.'
            : 'Esses clientes deixam de ver a aba no portal e de receber o lembrete. Os PDFs já enviados continuam guardados.'}
          {already > 0 && (
            <div style={{ marginTop: 8, color: '#8c8c8c' }}>
              {already} {already === 1 ? 'já está' : 'já estão'} {enabled ? 'com a aba ligada' : 'com a aba desligada'}; {toChange === 1 ? 'muda 1' : 'mudam ' + toChange}.
            </div>
          )}
        </div>
      ),
      okText: enabled ? 'Ligar' : 'Desligar',
      okType: enabled ? 'primary' : 'danger',
      cancelText: 'Cancelar',
      onOk: () => {
        setBulkLoading(true);
        const ids = chosen.map(c => c.id);
        return request('POST', 'extrato-bancario/clientes', { enabled, customer_ids: ids }, false)
          .then((response) => {
            props.setCustomersFields(ids, {
              extrato_bancario: enabled ? 1 : 0,
              extrato_bancario_changed_by: user.id,
              extrato_bancario_changed_by_name: user.name,
              extrato_bancario_changed_at: moment().format('YYYY-MM-DD HH:mm:ss'),
            });
            setSelected({});
            const result = response.data.data || {};
            if (enabled && result.module_enabled === false) {
              Modal.info({
                title: 'Falta ligar a função',
                content: 'Os clientes foram marcados, mas a aba só aparece no portal depois que a função for ligada em Configurações › Extrato Bancário.',
              });
            }
          })
          .catch(() => {})
          .finally(() => setBulkLoading(false));
      },
    });
  };

  const exportSelected = async () => {
    try {
      const response = await request('POST', '/customers/export', { customer_ids: selectedIds }, true, { responseType: 'blob' });
      FileDownload(response.data, 'Clientes_selecionados_' + moment().format('DD_MM_YYYY_HH-mm-ss') + '.xlsx');
    } catch (error) {
      message.error('Não foi possível exportar os clientes para Excel. Tente novamente mais tarde.');
    }
  };

  // Mesmas chaves da gaveta do cliente, aplicadas aos escolhidos.
  const bulkFlag = (field, enabled) => {
    const flag = BULK_FLAGS.filter(item => item.field === field)[0];
    const chosen = formattedCustomers.filter(c => selected[c.id]);
    const already = chosen.filter(c => !!Number(c[field]) === enabled).length;
    const toChange = chosen.length - already;
    const n = chosen.length;

    Modal.confirm({
      title: (enabled ? 'Ligar' : 'Desligar') + ' “' + flag.label + '” para ' + n + (n === 1 ? ' cliente?' : ' clientes?'),
      content: (
        <div>
          {flag.help}
          {already > 0 && (
            <div style={{ marginTop: 8, color: '#8c8c8c' }}>
              {already} {already === 1 ? 'já está' : 'já estão'} {enabled ? 'ligado' + (already === 1 ? '' : 's') : 'desligado' + (already === 1 ? '' : 's')}; {toChange === 1 ? 'muda 1' : 'mudam ' + toChange}.
            </div>
          )}
        </div>
      ),
      okText: enabled ? 'Ligar' : 'Desligar',
      okType: enabled ? 'primary' : 'danger',
      cancelText: 'Cancelar',
      onOk: () => {
        const ids = chosen.map(c => c.id);
        return request('POST', '/customers/bulk/flag', { customer_ids: ids, field, enabled }, false)
          .then(() => {
            const fields = {};
            fields[field] = enabled ? 1 : 0;
            if (field === 'allow_nf') {
              fields.allow_nf_changed_by = user.id;
              fields.allow_nf_changed_by_name = user.name;
              fields.allow_nf_changed_at = moment().format('YYYY-MM-DD HH:mm:ss');
            }
            props.setCustomersFields(ids, fields);
            setSelected({});
          })
          .catch(() => {});
      },
    });
  };

  const onBulkMenu = (key) => {
    const parts = key.split(':');
    if (parts[0] === 'extrato') {
      bulkExtrato(parts[1] === 'on');
    } else {
      bulkFlag(parts[0], parts[1] === 'on');
    }
  };

  const bulkActions = [{ label: 'Exportar', icon: 'file-excel', onClick: exportSelected }];
  if (canBulkExtrato) {
    bulkActions.push({
      label: 'Extrato Bancário',
      icon: 'bank',
      loading: bulkLoading,
      onClick: onBulkMenu,
      menu: [
        { key: 'extrato:on', label: 'Ligar a aba no portal', icon: 'check' },
        { key: 'extrato:off', label: 'Desligar a aba no portal', icon: 'stop', danger: true },
      ],
    });
  }
  if (canEditCustomers) {
    bulkActions.push({
      label: 'Alterar',
      icon: 'setting',
      onClick: onBulkMenu,
      menu: BULK_FLAGS.map(item => ({
        key: item.field,
        label: item.label,
        children: [
          { key: item.field + ':on', label: 'Ligar', icon: 'check' },
          { key: item.field + ':off', label: 'Desligar', icon: 'stop', danger: true },
        ],
      })),
    });
  }


  const onAfterOpen = (target) => {
    if (target.dataset.tour === 'import-excel') {
      const dropdownButton = document.querySelector('[data-tour="cadastrar-cliente"] .ant-dropdown-trigger');
      if (dropdownButton) {
        dropdownButton.click();
        setTimeout(() => {
          const dropdownMenu = document.querySelector('.ant-dropdown-menu');
          if (dropdownMenu) {
            dropdownMenu.style.display = 'block';
          }
        }, 100);
      }
    }
  };

  const handleTourClick = (e) => {
    if (e.target.closest('[data-tour="cadastrar-cliente"]')) {
      setIsTourOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleTourClick);
    return () => {
      document.removeEventListener('click', handleTourClick);
    };
  }, []);

  const handleCloseTasks = () => {
    setTasksDrawer({
      visible: false,
      customer: null
    });
  };

  return (
    <div>
      <ImportModal
        visible={importCSV}
        onCancel={handleCancel}
      />
      <Tour
        steps={steps}
        isOpen={isTourOpen}
        onRequestClose={() => setIsTourOpen(false)}
        accentColor="#722fd1"
        onAfterOpen={onAfterOpen}
      />
          <ModuleHeader
            breadcrumbs={['Clientes']}
            title="Clientes"
            search={
              <Search
                value={searchInput}
                placeholder="Pesquisar clientes"
                onChange={e => setSearchInput(e.target.value)}
              />
            }
            center={
              <>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  content={
                    <div style={{ width: 300 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                        {!persisted.isDefault && (
                          <a
                            style={{ fontSize: 12, color: '#f5222d' }}
                            onClick={handleClearAll}
                          >
                            Limpar tudo
                          </a>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {[
                            { value: 1, label: 'Ativos', color: '#a0d911', desc: 'Clientes ativos.', count: statusCounts[1] },
                            { value: 2, label: 'Pendentes', color: '#722ed1', desc: 'Clientes em processo no Dep. Societário.', count: statusCounts[2] },
                            { value: 0, label: 'Inativos', color: '#f5222d', desc: 'Clientes que não são mais seus clientes.', count: statusCounts[0] },
                          ].map(function(opt) {
                            var isActive = actived === opt.value;
                            return (
                              <div
                                key={opt.value}
                                onClick={function() { setActived(opt.value); }}
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                                  border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                  background: isActive ? opt.color + '0a' : '#fafafa',
                                  transition: 'all 0.2s',
                                }}
                              >
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? '#262626' : '#8c8c8c' }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt.color, display: 'inline-block', marginRight: 8 }} />
                                    {opt.label}
                                  </div>
                                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2, marginLeft: 16 }}>{opt.desc}</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? opt.color : '#bfbfbf' }}>{opt.count}</span>
                                  {isActive && <Icon type="check" style={{ color: opt.color, fontSize: 14 }} />}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  }
                >
                  <Badge
                    count={activeFilterCount}
                    offset={[-4, 0]}
                    style={{ backgroundColor: '#722ed1' }}
                  >
                    <Button icon="bars" style={{ borderRadius: 8 }}>Filtros</Button>
                  </Badge>
                </Popover>
                <Dropdown
                  trigger={['click']}
                  overlay={
                    <Menu>
                      <Menu.Item key="copy" onClick={handleCopyToClipBoard}>
                        <Icon type="copy" /> Área do cliente
                      </Menu.Item>
                      <Menu.Divider />
                      {canCreateCustomers && (
                        <Menu.Item key="1" onClick={function() { props.editCustomer({}); }}>
                          <Icon type="plus" /> Cadastro Manual
                        </Menu.Item>
                      )}
                      {canCreateCustomers && (
                        <Menu.Item key="2" onClick={function() { setImportCSV(true); }} data-tour="import-excel">
                          <Icon type="upload" /> Importar via Excel
                        </Menu.Item>
                      )}
                      <Menu.Item key="3" onClick={exportToExcel}>
                        <Icon type="file-excel" /> Exportar para Excel
                      </Menu.Item>
                    </Menu>
                  }
                >
                  <Button icon="plus" shape="circle" data-tour="cadastrar-cliente" />
                </Dropdown>
              </>
            }
            advanced={
              (function() {
                var STATUS_MAP = { 1: { label: 'Ativos', color: '#a0d911' }, 2: { label: 'Pendentes', color: '#722ed1' }, 0: { label: 'Inativos', color: '#f5222d' } };
                var current = STATUS_MAP[actived] || STATUS_MAP[1];
                var pills = [
                  { key: 'status', label: current.label, color: current.color, onRemove: actived !== 1 ? function() { setActived(1); } : undefined },
                ];
                if (onlyNoRule) {
                  pills.push({ key: 'rule', label: 'Sem regra', color: '#d46b08', icon: 'thunderbolt', onRemove: function() { setOnlyNoRule(false); } });
                }
                if (search) {
                  pills.push({ key: 'search', label: 'Busca: ' + search, color: '#722ed1', icon: 'search', onRemove: clearSearch });
                }
                return React.createElement(FilterPillsBar, {
                  pills: pills,
                  onClearAll: !persisted.isDefault ? handleClearAll : undefined,
                });
              })()
            }
          />
      <RestoredFiltersNotice
        visible={persisted.wasRestored}
        summary={restoredSummary}
        onReset={handleClearAll}
        onDismiss={persisted.dismissRestored}
      />
      {actived === 1 && (noRuleCount > 0 || onlyNoRule) && (
        <div data-customers-rule-bar style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, margin: '0 0 12px', padding: '10px 14px',
          borderRadius: 10, border: '1px solid ' + (noRuleCount > 0 ? '#ffd591' : '#d9f7be'), background: noRuleCount > 0 ? '#fff7e6' : '#f6ffed',
        }}>
          <Icon type={noRuleCount > 0 ? 'thunderbolt' : 'check-circle'} style={{ color: noRuleCount > 0 ? '#d46b08' : '#389e0d', fontSize: 16 }} />
          <span style={{ flex: '1 1 320px', fontSize: 13, color: '#262626' }}>
            {noRuleCount > 0 ? (
              <>
                <strong>{noRuleCount === 1 ? '1 cliente ativo sem regra' : noRuleCount + ' clientes ativos sem regra'}</strong>
                {': o sistema não consegue sugerir tarefas para eles. '}
                <Tooltip title={CUSTOMER_RULE_HELP} overlayStyle={{ maxWidth: 320 }}>
                  <span tabIndex={0} style={{ color: '#ad4e00', textDecoration: 'underline dotted', cursor: 'help' }}>Por que toda empresa precisa de regra?</span>
                </Tooltip>
              </>
            ) : 'Todos os clientes ativos têm regra.'}
          </span>
          <Button size="small" type={onlyNoRule ? 'default' : 'primary'} ghost={!onlyNoRule}
            onClick={() => setOnlyNoRule(!onlyNoRule)} data-only-no-rule>
            {onlyNoRule ? 'Mostrar todos' : 'Ver só esses'}
          </Button>
        </div>
      )}
      <div className="table-clabs">
        <CustomersVirtualTable
          onOpenRule={actived === 1 ? openRule : undefined}
          data={filteredCustomers}
          isLoading={isLoading}
          actived={actived}
          styleCompanyName={styleCompanyName}
          onAction={onAction}
          canEditCustomers={canEditCustomers}
          canDeleteCustomers={canDeleteCustomers}
          findUser={props.findUser}
          filterCompanyType={filterCompanyType}
          selected={selected}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
          allSelected={allSelected}
          someSelected={filteredSelectedCount > 0}
          showExtrato={canBulkExtrato}
        />
      </div>
      <SelectionToolbar
        count={selectedCount}
        itemLabel="cliente"
        onClear={() => setSelected({})}
        extra={!allSelected && filteredCustomers.length > filteredSelectedCount ? (
          <a style={{ color: '#fff', textDecoration: 'underline' }} onClick={() => toggleAll(true)}>
            Selecionar todos os {filteredCustomers.length} da lista
          </a>
        ) : null}
        actions={bulkActions}
      />
      <CustomerTasks 
        visible={tasksDrawer.visible}
        customer={tasksDrawer.customer}
        onClose={handleCloseTasks}
      />
    </div>
    )
}

const mapStateToProps = state => {
  return{
    customers: state.customers.list.filter(row => row.is_customer),
    isLoading: state.customers.isLoading,
    modalInactivate: state.customersnew.modalInactivate,
    findUser: (id) => state.users.filter(r=>r.id===id)[0],
    user: state.user,
    companyTypes: state.settings['leads.company_type'],
  }
}

const mapDispatchProps = dispatch => {
  return{
    loadCustomers: () => dispatch(customersActions.loadCustomers()),
    deleteCustomer: (id) => dispatch({
      type: 'DELETE_CUSTOMER',
      payload: {
        request:{
          method: 'delete',
          url:'/customer/'+id,
        }
      },
      id: id
    }),
    inactivateCustomer: (id) => dispatch({
      type: 'INACTIVATE_CUSTOMER',
      payload: {
        request:{
          method: 'put',
          url:'/customer/'+id+'/inactivate',
        }
      },
      id: id
    }),
    activateCustomer: (id) => dispatch({
      type: 'ACTIVATE_CUSTOMER',
      payload: {
        request:{
          method: 'put',
          url:'/customer/'+id+'/activate',
        }
      },
      id: id
    }),
    forceActivateCustomer: (id) => dispatch({
      type: 'FORCE_ACTIVATE_CUSTOMER',
      payload: {
        request:{
          method: 'put',
          url:'/customer/'+id+'/force-activate',
        }
      },
      id: id
    }),
    editCustomer: (data) => dispatch(customersActions.showDrawerCustomer(data)),
    setCustomersFields: (ids, fields) => dispatch({
      type: 'SET_CUSTOMERS_FIELDS',
      payload: { ids, fields }
    }),
  }
}

export default connect(mapStateToProps, mapDispatchProps)(CustomersList);